import { Alert, Button, Checkbox, Input, List, Modal, Space, Spin, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCheck, faCloudDownload } from '@fortawesome/pro-solid-svg-icons'
import { blue } from '@ant-design/colors'
import { useUnlinkedQboCustomersQuery } from '../../domain/quickbooks/quickbooksQueries'
import { useImportQboCustomersMutation } from './customerQueries'
import {
  allSelected,
  filterQboCustomers,
  hiddenSelectionCount,
  qboImportErrorMessage,
  selectedQboCustomers,
  toggleSelectAll,
  toggleSelection,
} from './importQboCustomersModel'

const { Text } = Typography

type Props = {
  open: boolean
  onClose: () => void
  onImported: () => void
}

const ImportQuickBooksCustomersModal = ({ open, onClose, onImported }: Props) => {
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const qboCustomersQuery = useUnlinkedQboCustomersQuery(open)
  const importMutation = useImportQboCustomersMutation()

  const customers = qboCustomersQuery.data ?? []
  const visible = useMemo(() => filterQboCustomers(customers, search), [customers, search])
  const visibleIds = visible.map(c => c.id)
  const hiddenSelected = hiddenSelectionCount(selectedIds, visibleIds)

  useEffect(() => {
    if (open) {
      setSearch('')
      setSelectedIds([])
    }
  }, [open])

  // Notifications and tracking live on the mutation; only the dialog's own reaction is here.
  const importSelected = () => {
    importMutation.mutate(selectedQboCustomers(customers, selectedIds), {
      onSuccess: () => {
        onImported()
        onClose()
      },
      onError: () => {
        // A partial failure still imports the rest, so refresh and start the picker over.
        setSelectedIds([])
        onImported()
      },
    })
  }

  return <Modal
    title={<Space><FontAwesomeIcon icon={faCloudDownload} /> Import Customers from QuickBooks</Space>}
    open={open}
    onCancel={onClose}
    footer={<>
      <Button onClick={onClose} icon={<FontAwesomeIcon icon={faArrowLeft} />}>Close</Button>
      <Button
        type="primary"
        onClick={importSelected}
        loading={importMutation.isLoading}
        disabled={selectedIds.length === 0}
        icon={<FontAwesomeIcon icon={faCheck} />}
      >
        Import Selected ({selectedIds.length})
      </Button>
    </>}
  >
    {qboCustomersQuery.isError
      ? <Alert type="error" showIcon message={qboImportErrorMessage(qboCustomersQuery.error, 'There was an error getting customers from QuickBooks')} />
      : <Spin spinning={qboCustomersQuery.isFetching}>
        <Input
          value={search}
          placeholder="Search QuickBooks customers..."
          style={{ marginBottom: 16 }}
          onChange={e => setSearch(e.target.value)}
        />
        {visible.length > 0 && <Checkbox
          checked={allSelected(selectedIds, visibleIds)}
          onChange={() => setSelectedIds(prev => toggleSelectAll(prev, visibleIds))}
          style={{ marginBottom: 16 }}
        >
          Select all customers
        </Checkbox>}
        {hiddenSelected > 0 && <div style={{ marginBottom: 8 }}>
          <Text type="secondary">{selectedIds.length} selected ({hiddenSelected} hidden by search)</Text>
        </div>}
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          <List
            dataSource={visible}
            locale={{ emptyText: qboCustomersQuery.isFetching ? ' ' : customers.length === 0 ? 'No QuickBooks customers left to import' : 'No QuickBooks customers match your search' }}
            renderItem={item => {
              const selected = selectedIds.includes(item.id)
              return <List.Item
                onClick={() => setSelectedIds(prev => toggleSelection(prev, item.id))}
                style={{ cursor: 'pointer', background: selected ? blue[1] : 'transparent', padding: 16 }}
              >
                {/* The row owns the click; an interactive checkbox inside it would toggle twice. */}
                <Checkbox checked={selected} style={{ marginRight: 8, pointerEvents: 'none' }} />
                {item.displayName}
              </List.Item>
            }}
          />
        </div>
      </Spin>}
  </Modal>
}

export default ImportQuickBooksCustomersModal
